import { apiClient } from './client'
import type { AiJobStatusResponse, PromptType, AiUsage } from '@/types/ai'
import type {
  PracticeExerciseSetDetail,
  PracticeExerciseSetSummary,
  SubmitPracticeSetPayload,
} from '@/types/practiceExerciseSet'
import type { WordPopup } from '@/types/content'
import type { PracticeUnitRef } from '@/types/practiceUnit'

export interface ExplainWordPayload {
  wordId: string
  promptType?: PromptType
  /** BCP-47 target language for IN_NATIVE prompt (defaults to 'en') */
  targetLanguage?: string
}

export interface GenerateStoryPayload {
  units?: PracticeUnitRef[]
  /** @deprecated Prefer units */
  wordIds?: string[]
  cefrLevel?: string
  /** Band A grammar focus (G4b); applied when grammar_targeted_practice is on. */
  patternIds?: string[]
}

export interface GenerateExercisesPayload {
  units?: PracticeUnitRef[]
  /** @deprecated Prefer units */
  wordIds?: string[]
  cefrLevel?: string
  exerciseCount?: number
  difficulty?: 'easy' | 'medium' | 'hard'
  /** Band A grammar focus (G4a); applied when grammar_targeted_practice is on. */
  patternIds?: string[]
}

export const aiApi = {
  // ── Async word explanation ──────────────────────────────────────────────

  explainWord: (payload: ExplainWordPayload) =>
    apiClient.post<{ jobId: string }>('/ai/explain', {
      wordId: payload.wordId,
      promptType: payload.promptType ?? 'DEFAULT',
      targetLanguage: payload.targetLanguage ?? 'en',
    }),

  getJobStatus: (jobId: string) =>
    apiClient.get<AiJobStatusResponse>(`/ai/jobs/${jobId}`),

  getUsage: () => apiClient.get<AiUsage>('/ai/usage').then((r) => r.data),

  submitFeedback: (explanationId: string, feedback: 1 | -1) =>
    apiClient.post<void>('/ai/explain/feedback', { explanationId, feedback }),

  // ── AI Practice (AIPRAC-02, AIPRAC-03) ─────────────────────────────────

  generateStory: (payload: GenerateStoryPayload) =>
    apiClient.post<{ jobId: string }>('/ai/practice/story', payload),

  generateExercises: (payload: GenerateExercisesPayload) =>
    apiClient.post<{ jobId: string }>('/ai/practice/exercises', {
      ...payload,
      exerciseCount: payload.exerciseCount ?? 7,
    }),

  listExerciseSets: (limit = 10) =>
    apiClient.get<PracticeExerciseSetSummary[]>('/ai/practice/exercise-sets', { params: { limit } }),

  getExerciseSet: (id: string) =>
    apiClient.get<PracticeExerciseSetDetail>(`/ai/practice/exercise-sets/${id}`),

  updateExerciseSetProgress: (id: string, progressJson: string) =>
    apiClient.put<void>(`/ai/practice/exercise-sets/${id}/progress`, { progressJson }),

  submitExerciseSet: (id: string, payload: SubmitPracticeSetPayload) =>
    apiClient.post<PracticeExerciseSetSummary>(`/ai/practice/exercise-sets/${id}/submit`, payload),

  getWordPopup: (word: string, context?: string) =>
    apiClient.get<WordPopup>('/ai/practice/word-popup', {
      params: { word, context },
    }),

  recordStoryEncounters: (words: string[]) =>
    apiClient.post<void>('/ai/practice/story-encounters', { words }),
}

